import React from 'react';

import {IdcardOutlined, LockOutlined} from '@ant-design/icons/lib';
import {Button, Card} from 'antd';
import {confirmResetPassword, resetPassword} from 'aws-amplify/auth';
import type {FormikProps} from 'formik';
import {withFormik} from 'formik';
import {Form, FormItem, Input} from 'formik-antd';
import * as yup from 'yup';

import {AnchorLink} from '../AnchorLink';

import {Extra} from './Shared';

import {getErrorInfo} from 'lib/errorUtils';
import type {IAuthProps} from 'types';
import {AuthState} from 'types';

interface IFormValues {
  email: string;
  code: string;
  password: string;
}

// eslint-disable-next-line react-refresh/only-export-components
const C: React.FC<FormikProps<IFormValues> & IAuthProps> = ({
  isSubmitting,
  setSubmitting,
  setState: setStateCallback,
  values,
  email
}) => {
  const setState = setStateCallback;
  const resendCode = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!values.email) return;
    setSubmitting(true);
    try {
      await resetPassword({username: values.email});
    } finally {
      setSubmitting(false);
    }
  };
  const backToSignIn = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setState({authState: AuthState.signIn, email: values.email});
  };
  return (
    <Card
      title="Reset your password"
      extra={<AnchorLink onClick={backToSignIn}>Back to Sign In</AnchorLink>}>
      <Form layout="vertical">
        {!email && (
          <FormItem name="email" label="Email">
            <Input autoFocus name="email" type="email" size="large" prefix={<IdcardOutlined />} />
          </FormItem>
        )}
        <FormItem name="code" label="Code">
          <Input name="code" type="text" size="large" prefix={<LockOutlined />} />
        </FormItem>
        <FormItem name="password" label="New password">
          <Input.Password name="password" type="password" size="large" prefix={<LockOutlined />} />
        </FormItem>

        <Button block size="large" type="primary" loading={isSubmitting} htmlType="submit">
          Change password
        </Button>
        <Extra>
          Lost your code?{' '}
          <AnchorLink
            onClick={(e: React.SyntheticEvent) => {
              void resendCode(e);
            }}>
            Resend code
          </AnchorLink>
        </Extra>
      </Form>
    </Card>
  );
};

const schema = yup.object().shape({
  code: yup.string().required(),
  password: yup.string().min(6).required()
});

export const ForgotPasswordSubmit = withFormik<IAuthProps, IFormValues>({
  validationSchema: schema,
  mapPropsToValues: ({email = '', code = ''}) => ({email, code, password: ''}),
  handleSubmit: async ({email, code, password}, {props, setErrors, setSubmitting}) => {
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: password
      });
      props.setState({authState: AuthState.signIn, email});
    } catch (error: unknown) {
      // Safely extract error information
      const {name: errorName, message: errorMessage} = getErrorInfo(error);

      // Handle specific error types
      if (errorName === 'CodeMismatchException') {
        setErrors({code: errorMessage});
        return;
      }

      setErrors({password: errorMessage});
    } finally {
      setSubmitting(false);
    }
  }
})(C);
